import { Component, HostListener } from '@angular/core';
import { RouterModule } from '@angular/router';
import { inject } from '@angular/core';
import { CookieConsentService } from '@app/services/ui/cookie-consent/cookie-consent.service';

import { AccordionState, FooterSection } from '@app/interfaces/public/layout.interfaces';

@Component({
  selector: 'main-footer',
  imports: [RouterModule],
  templateUrl: './main-footer.component.html',
  styleUrl: './main-footer.component.scss',
})
export class MainFooter {
  private readonly cookieConsent = inject(CookieConsentService);
  readonly currentYear = new Date().getFullYear();
  readonly footerSections: FooterSection[] = [
    {
      key: 'products',
      title: 'Products',
      links: [
        { label: 'Educational Solutions', route: '/solutions/educational-solution' },
        { label: 'Industrial Solutions', route: '/solutions/industrial-solution' },
        { label: 'Product Catalogs', route: '/product-catalogs' },
        {
          label: 'Courses',
          route: '/products/courses/higher-education',
          children: [
            { label: 'Higher Education', route: '/products/courses/higher-education' },
            { label: 'TVET', route: '/products/courses/tvet' },
            { label: 'Basic Education', route: '/products/courses/basic-education' },
          ],
        },
        { label: 'Specialization', route: '/product-categories' },
      ],
    },
    {
      key: 'partners',
      title: 'Partners',
      hideOnMobile: true,
      links: [
        { label: 'Academe Partners', route: '/partners/academe' },
        { label: 'Industrial Partners', route: '/partners/industrial' },
      ],
    },
    {
      key: 'news',
      title: 'News & Updates',
      links: [
        { label: 'All News & Updates', route: '/news-and-updates' },
        {
          label: 'Announcements',
          route: '/news-and-updates',
          queryParams: { filter: 'announcement' },
        },
        {
          label: 'Educational News',
          route: '/news-and-updates',
          queryParams: { tab: 'educational' },
        },
        {
          label: 'Industrial News',
          route: '/news-and-updates',
          queryParams: { tab: 'industrial' },
        },
      ],
    },
    {
      key: 'trainings',
      title: 'Trainings & Events',
      links: [
        { label: 'All Trainings & Events', route: '/trainings' },
        { label: 'Upcoming Events', route: '/trainings', fragment: 'upcoming-events' },
        { label: 'Past Events', route: '/trainings', fragment: 'past-events' },
      ],
    },
    {
      key: 'tour',
      title: 'Virtual Tour',
      hideOnMobile: true,
      links: [{ label: 'Start Virtual Tour', route: '/virtual-tour' }],
    },
    {
      key: 'company',
      title: 'Company',
      mobileTitle: 'Our Company',
      links: [
        { label: 'About Us', route: '/about-us' },
        { label: 'Sponsorship', route: '/company/sponsorship' },
        { label: 'Team', route: '/company/team' },
        { label: 'Our Contacts', route: '/company/our-contacts' },
      ],
    },
    {
      key: 'hirings',
      title: 'Hirings',
      links: [{ label: 'Open Positions', route: '/company/careers' }],
    },
  ];

  readonly expandedSections: AccordionState = {};
  isMobile = false;

  constructor() {
    this.updateViewportState();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateViewportState();
  }

  toggleSection(sectionKey: string): void {
    if (!this.isMobile) {
      return;
    }

    this.expandedSections[sectionKey] = !this.expandedSections[sectionKey];
  }

  isSectionExpanded(sectionKey: string): boolean {
    return !this.isMobile || Boolean(this.expandedSections[sectionKey]);
  }

  openCookiePreferences(): void {
    this.cookieConsent.openPreferences();
  }

  private updateViewportState(): void {
    this.isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  }
}
